import React, {useEffect, useMemo, useState} from 'react';
import {Redirect, Route, Switch, withRouter} from "react-router-dom";
import {useBootstrap, useEntities, useEntity, useFilters, useProgress, useRenderer} from "@atttomyx/shared-hooks";
import {usePushEvent} from "@atttomyx/react-hooks";
import {
    AboutPage,
    ChangePasswordPage,
    ForgotPasswordPage,
    LoginPage,
    NotFoundPage,
    NotificationsPage,
    PhoneStatusBar,
    ProfilePage,
    PublicHeader,
    WelcomePage,
} from "@atttomyx/react-components";
import {HelpDialog} from "./components/dialogs";
import {UserSettingsForm} from "./components/forms";
import {Logo, Splash} from "./components/misc";
import {Footer} from "./components/nav";
import {
    AddContactPage,
    AddTemplatePage,
    AddUserPage,
    ContactsPage,
    CoursesPage,
    EditContactPage,
    EditCoursePage,
    EditQuestionnairePage,
    EditTemplatePage,
    EditUserPage,
    FilesPage,
    PendingPage,
    QuestionnairesPage,
    TemplatesPage,
    ThankYouPage,
    UsersPage
} from "./components/pages";
import {
    accountService,
    appService,
    authService,
    courseService,
    documentService,
    mobileService,
    nodeService,
    notificationService,
    profileService,
    progressService,
    questionnaireService,
    questionService,
    submissionService,
    templateService,
    upsellService,
    userService,
    videoService
} from "./services";
import {arrays, datetime, notifications as notificationUtils, objects, sorting, strings} from "@atttomyx/shared-utils";
import {branding as brandingUtils, firebase as firebaseUtils, mobile, router, timezones} from "@atttomyx/react-utils";
import {sortingUtils} from "./utils";
import {
    CONTACT_FILTERS,
    COURSE_FILTERS,
    FILE_FILTERS,
    getUserFilters,
    QUESTIONNAIRE_FILTERS,
    TEMPLATE_FILTERS
} from "./filters";
import {delays, themes, time} from "@atttomyx/shared-constants";
import {app} from "./konstants";
import {
    ANSWER_TYPE_INTEGER,
    FIREBASE_CONFIG,
    NOTIFICATION_TYPES,
    PAGE_ABOUT,
    PAGE_CONTACT,
    PAGE_CONTACTS,
    PAGE_COURSE,
    PAGE_COURSES,
    PAGE_FILES,
    PAGE_LOGIN,
    PAGE_NOTIFICATIONS,
    PAGE_PASSWORD,
    PAGE_QUESTIONNAIRE,
    PAGE_QUESTIONNAIRES,
    PAGE_RECOVERY,
    PAGE_SETTINGS,
    PAGE_TEMPLATE,
    PAGE_TEMPLATES,
    PAGE_THANK_YOU,
    PAGE_USER,
    PAGE_USERS,
    PAGE_WELCOME,
    PATH_DEEP_LINK,
    PRIVATE_PAGES_EXACT,
    PRIVATE_PAGES_STARTS_WITH,
    PUBLIC_PAGES_EXACT,
    PUBLIC_PAGES_STARTS_WITH,
    STATUS_ACTIVE,
    TOPICS,
    TYPE_INVENTOR,
    TYPE_INVESTOR
} from "./constants";
import "./app.css";

const MOCK_PHONE = false;

const App = (props) => {
    const { snackbar, dimensions, history, onThemeChange } = props;
    const [ showHelp, setShowHelp ] = useState(false);
    const [ actor, setActor ] = useState(null);

    const users = useEntities(userService.listUsers, snackbar.setError, "users", sorting.sortByLastNameAndFirstName);
    const logins = useEntities(authService.listLogins, snackbar.setError, "logins", sorting.sortByUpdatedDesc);
    const notifications = useEntities(notificationService.listNotifications, snackbar.setError, "notifications", sorting.sortByCreatedDesc);
    const courses = useEntities(courseService.listCourses, snackbar.setError, "courses", sorting.sortByOrder);
    const documents = useEntities(documentService.listDocuments, snackbar.setError, "documents", sorting.sortByOrder);
    const files = useEntities(documentService.mineDocuments, snackbar.setError, "documents", sortingUtils.sortByFileName);
    const videos = useEntities(videoService.listVideos, snackbar.setError, "videos", sorting.sortByOrder);
    const upsells = useEntities(upsellService.listUpsells, snackbar.setError, "upsells", sorting.sortByOrder);
    const questionnaires = useEntities(questionnaireService.listQuestionnaires, snackbar.setError, "questionnaires", sorting.sortByOrder);
    const questions = useEntities(questionService.listQuestions, snackbar.setError, "questions", sorting.sortByOrder);
    const templates = useEntities(templateService.listTemplates, snackbar.setError, "templates", sorting.sortByOrder);
    const progresses = useEntities(progressService.listProgresses, snackbar.setError, "progresses", sorting.sortByCreated);
    const nodes = useEntities(nodeService.listNodes, snackbar.setError, "nodes", sorting.sortByLastNameAndFirstName);

    const chronicle = useEntity(progressService.loadMine, snackbar.setError);

    const questionnaireRenderer = useRenderer(questionnaires, (questionnaire) => questionnaire.title);

    const keyToMetadata = useMemo(() => {
        return getUserFilters(questions.entities);
    }, [questions.entities]);

    const userFilters = useFilters(keyToMetadata);
    const contactFilters = useFilters(CONTACT_FILTERS);
    const courseFilters = useFilters(COURSE_FILTERS);
    const questionnaireFilters = useFilters(QUESTIONNAIRE_FILTERS);
    const fileFilters = useFilters(FILE_FILTERS);
    const templateFilters = useFilters(TEMPLATE_FILTERS);

    const submissionsLoader = (cursor, limit, success, failure) => {
        const loader = actor.roles.admin || actor.roles.investor
            ? submissionService.listSubmissions
            : submissionService.mineSubmissions;

        loader(cursor, limit, success, failure);
    };

    const submissions = useEntities(submissionsLoader, snackbar.setError, "submissions", sorting.sortByCreatedDesc);

    const clearAll = () => {
        progress.clear();
        bootstrap.clear();
        users.clear();
        logins.clear();
        notifications.clear();
        courses.clear();
        documents.clear();
        videos.clear();
        upsells.clear();
        questionnaires.clear();
        questions.clear();
        submissions.clear();
        templates.clear();
        progresses.clear();
        nodes.clear();
        files.clear();
        chronicle.clear();
        userFilters.clear();
        contactFilters.clear();
        courseFilters.clear();
        questionnaireFilters.clear();
        fileFilters.clear();
        templateFilters.clear();
    };

    const onLogout = () => {
        authService.cancelAllRequests("logged out");
        authService.clearAuthToken();
        timezones.clearTimeZone();

        clearAll();

        router.redirectToInitialPage(history, PUBLIC_PAGES_EXACT, PUBLIC_PAGES_STARTS_WITH, PAGE_LOGIN);

        if (mobile.isMobile()) {
            mobile.lifecycle(true, false);
        }
    };

    const loadAll = () => {
        notifications.refresh();
        questionnaires.refresh();
        questions.refresh();
        submissions.refresh();

        if (actor.roles.admin) {
            logins.refresh();
            templates.refresh();
            progresses.refresh();
            nodes.refresh();

        } else {
            logins.empty();
            templates.empty();
            progresses.empty();
            nodes.empty();
        }

        if (actor.roles.admin || actor.roles.investor) {
            users.refresh();

        } else {
            users.empty();
        }

        if (actor.roles.admin || actor.roles.inventor) {
            courses.refresh();
            documents.refresh();
            videos.refresh();
            upsells.refresh();

        } else {
            courses.empty();
            documents.empty();
            videos.empty();
            upsells.empty();
        }

        if (actor.roles.inventor) {
            files.refresh();
            chronicle.refresh();

        } else {
            files.empty();
            chronicle.empty();
        }
    };

    const bootstrapSuccess = () => {
        if (bootstrap.user.temporary) {
            router.redirectTo(history, PAGE_PASSWORD);

        } else {
            router.redirectToInitialPage(history, PRIVATE_PAGES_EXACT, PRIVATE_PAGES_STARTS_WITH, home);
        }

        if (mobile.isMobile()) {
            mobile.lifecycle(true, true);
        }

        authService.recordLoginIfNecessary(snackbar.setError);
    };

    const bootstrapFailure = (err) => {
        snackbar.setError(err);
        onLogout();
    };

    const bootstrap = useBootstrap(authService, accountService, profileService, notificationService,
        bootstrapSuccess, bootstrapFailure);

    const progress = useProgress(bootstrap,
        [users, logins, notifications, courses, documents, videos, upsells, questionnaires, questions, submissions, templates, progresses, files, nodes],
        [chronicle],
        2 * time.MILLISECONDS_IN_SECOND,
        delays.millisecondsAnimationShort);

    const event = usePushEvent(bootstrap);

    const inventors = useMemo(() => {
        return users.entities.filter(user => user.settings.type === TYPE_INVENTOR);
    }, [users.entities]);

    const filteredUsers = useMemo(() => {
        return !actor
            ? []
            : actor.roles.admin
                ? users.entities
                : inventors;
    }, [actor, users.entities, inventors]);

    const filteredCourses = useMemo(() => {
        return !actor
            ? []
            : actor.roles.admin
                ? courses.entities
                : courses.entities.filter(course => course.status === STATUS_ACTIVE);
    }, [actor, courses.entities]);

    const filteredQuestionnaires = useMemo(() => {
        return !actor
            ? []
            : actor.roles.admin
                ? questionnaires.entities
                : questionnaires.entities.filter(questionnaire => questionnaire.status === STATUS_ACTIVE);
    }, [actor, questionnaires.entities]);

    const decoratedUsers = useMemo(() => {
        const usersById = arrays.getIdToEntity(filteredUsers);
        const decoratedById = objects.deepCopy(usersById);

        Object.values(decoratedById).forEach(decorated => {
            decorated.questions = {};
            decorated.login = null;
        });

        submissions.entities.forEach(submission => {
            const userId = submission.userId;

            if (userId) {
                const decorated = decoratedById[userId];

                if (decorated) {
                    submission.questions.forEach(question => {
                        if (question.questionId) {
                            decorated.questions[question.questionId] = question.reply;
                        }
                    });
                }
            }
        });

        logins.entities.forEach(login => {
            const userId = login.userId;

            if (userId) {
                const decorated = decoratedById[userId];

                if (decorated) {
                    decorated.login = datetime.sanitizeTimestamp(login.updated);
                }
            }
        });

        return Object.values(decoratedById).sort(sorting.sortByLastNameAndFirstName);
    }, [filteredUsers, submissions.entities, logins.entities]);

    const submissionMergeFields = useMemo(() => {
        const outer = {};
        const questionsByQuestionnaireId = {};

        questions.entities.forEach(question => {
            const array = objects.defaultIfNullOrUndefined(questionsByQuestionnaireId[question.questionnaireId], []);

            arrays.addTo(array, question);
            questionsByQuestionnaireId[question.questionnaireId] = array;
        });

        questionnaires.entities
        .filter(questionnaire => questionnaire.status === STATUS_ACTIVE)
        .forEach(questionnaire => {
            const questionsToUse = questionsByQuestionnaireId[questionnaire.id];

            if (questionsToUse) {
                const inner = {};
                let sum = false;

                questionsToUse.forEach(question => {
                    inner[question.id] = question.ask;

                    if (question.type === ANSWER_TYPE_INTEGER) {
                        sum = true;
                    }
                });

                if (sum) {
                    inner.sum = "Sum";
                }

                outer[questionnaire.id] = {
                    label: questionnaire.title,
                    fields: inner,
                };
            }
        });

        console.log(outer);
        return outer;
    }, [questionnaires.entities, questions.entities]);

    const home = useMemo(() => {
        return actor && actor.roles.investor ? PAGE_USERS : PAGE_COURSES;
    }, [actor]);

    const hasNoRoles = useMemo(() => {
        return actor && !actor.roles.admin && !actor.roles.inventor && !actor.roles.investor;
    }, [actor]);

    const onLogin = (accounts) => {
        progress.refresh();
        bootstrap.refresh(accounts);
    };

    useEffect(() => {
        if (event && event.type) {
            const type = event.type;
            const data = event.payload || {};
            const payload = data.payload || {};
            const me = data.actor && actor && data.actor.id !== actor.userId;
            const admin = actor && actor.roles && actor.roles.admin;
            const investor = actor && actor.roles && actor.roles.investor;
            const inventor = actor && actor.roles && actor.roles.inventor;

            console.table(type, data);

            if (!me) {
                switch (type) {
                    case "user_saved":
                    case "user_deleted":
                        if (admin || investor) {
                            users.refresh();
                        }
                        break;
                    case "submission":
                        if (admin || investor) {
                            submissions.refresh();
                        }
                        break;
                    case "file_uploaded":
                    case "file_deleted":
                        if (admin || investor) {
                            documents.refresh();

                        } else if (inventor) {
                            files.refresh();
                        }
                        break;
                    case "contact_added":
                    case "contact_saved":
                    case "contact_deleted":
                        if (admin) {
                            nodes.refresh();
                        }
                    default:
                        break;
                }

                if (notificationUtils.isEnabled(actor, type)) {
                    const title = data.title;

                    if (strings.isNotBlank(title)) {
                        snackbar.setInfo(title);

                    } else {
                        console.log("No need to pop a message in the snackbar");
                    }

                    if (notificationUtils.isPersist(actor, type)) {
                        notifications.refresh();

                    } else {
                        console.log("No need to refresh notifications");
                    }
                } else {
                    console.log("Ignoring notification (disabled)");
                }
            } else {
                console.log("Ignoring notification (me)");
            }
        }
    }, [event]);

    useEffect(() => {
        if (bootstrap.user) {
            const modified = objects.deepCopy(bootstrap.user);

            if (bootstrap.user.roles.admin) {
                if (modified.settings.mode === TYPE_INVESTOR) {
                    modified.roles.admin = false;
                    modified.roles.investor = true;
                    modified.roles.inventor = false;

                } else if (modified.settings.mode === TYPE_INVENTOR) {
                    modified.roles.admin = false;
                    modified.roles.investor = false;
                    modified.roles.inventor = true;
                }
            }

            setActor(modified);

        } else {
            setActor(null);
        }
    }, [bootstrap.user]);

    useEffect(() => {
        if (actor) {
            loadAll();
        }
    }, [actor]);

    useEffect(() => {
        const settings = actor ? actor.settings || {} : {};
        const theme = settings.theme || themes.DARK;
        const branding = brandingUtils.getBranding(theme);

        if (mobile.isMobile()) {
            mobile.setBranding(theme, branding);
        }

        onThemeChange(theme, branding);
    }, [bootstrap.account, actor]);

    useEffect(() => {
        if (mobile.isMobile()) {
            mobile.syncProgress(progress.percent, progress.loading);
        }
    }, [progress.percent, progress.loading]);

    useEffect(() => {
        if (mobile.isMobile()) {
            mobileService.initMessagingTokenObserver(bootstrap);
        }
    }, [bootstrap]);

    useEffect(() => {
        appService.ensureOnLatestVersion(false, null);
        authService.setupAxiosInterceptors(onLogout);
        notificationUtils.initNotifications(NOTIFICATION_TYPES, TOPICS);
        firebaseUtils.initFirebase(FIREBASE_CONFIG);

        if (mobile.isMobile()) {
            mobile.initializeMessageListener();
        }

        if (authService.loggedIn()) {
            onLogin();

        } else {
            onLogout();
        }

        return () => {
            if (mobile.isMobile()) {
                mobile.deactivateMessageListener();
            }
        }
    }, []);

    return <div className="app">
        {progress.loading || progress.paused ?
            <Splash
                percent={progress.percent}
            /> : bootstrap.account && actor ?
            <>
                {MOCK_PHONE ? <PhoneStatusBar/> : null}
                <div className="content">
                    {hasNoRoles ?
                        <PendingPage
                            onLogout={onLogout}
                        /> :
                        <Switch>
                            <Route exact path="/">
                                <Redirect to={home}/>
                            </Route>
                            <Route path={PAGE_NOTIFICATIONS}>
                                <NotificationsPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    notifications={notifications.entities}
                                    onBulkSave={notifications.onEntitiesSaved}
                                    onDelete={notifications.onEntityDeleted}
                                    notificationService={notificationService}
                                />
                            </Route>
                        {actor.roles.admin || actor.roles.inventor ?
                            <Route path={PAGE_COURSES}>
                                <CoursesPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={actor}
                                    courses={filteredCourses}
                                    documents={documents.entities}
                                    videos={videos.entities}
                                    upsells={upsells.entities}
                                    questionnaires={filteredQuestionnaires}
                                    questions={questions.entities}
                                    submissions={actor.roles.inventor ? submissions.entities : []}
                                    files={files.entities}
                                    progress={chronicle.entity}
                                    filters={courseFilters}
                                    onSaveCourse={courses.onEntitySaved}
                                    onSaveSubmission={submissions.onEntitySaved}
                                    onSaveFiles={files.onEntitiesSaved}
                                    onSaveProgress={chronicle.onSave}
                                    onDelete={courses.onEntityDeleted}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route
                                path={`${PAGE_COURSE}/:id`}
                                render={(props) =>
                                    <EditCoursePage
                                        {...props}
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        user={actor}
                                        courses={courses.entities}
                                        documents={documents.entities}
                                        videos={videos.entities}
                                        upsells={upsells.entities}
                                        questionnaires={questionnaires.entities}
                                        questions={questions.entities}
                                        templates={templates.entities}
                                        filters={courseFilters}
                                        onSaveCourse={courses.onEntitySaved}
                                        onSaveDocument={documents.onEntitySaved}
                                        onSaveVideo={videos.onEntitySaved}
                                        onSaveUpsell={upsells.onEntitySaved}
                                        onPrev={(courseId) => router.redirectTo(history, PAGE_COURSE + "/" + courseId)}
                                        onNext={(courseId) => router.redirectTo(history, PAGE_COURSE + "/" + courseId)}
                                    />}
                            /> : null}
                        <Route path={PAGE_QUESTIONNAIRES}>
                            <QuestionnairesPage
                                snackbar={snackbar}
                                dimensions={dimensions}
                                user={actor}
                                questionnaires={filteredQuestionnaires}
                                questions={questions.entities}
                                submissions={actor.roles.inventor ? submissions.entities : []}
                                filters={questionnaireFilters}
                                onSaveQuestionnaire={questionnaires.onEntitySaved}
                                onCopyQuestionnaire={(result) => {
                                    questionnaires.onEntitySaved(result.questionnaire);
                                    questions.onEntitiesSaved(result.questions);
                                }}
                                onDeleteQuestionnaire={questionnaires.onEntityDeleted}
                                onSaveSubmission={submissions.onEntitySaved}
                                onDeleteSubmission={submissions.onEntityDeleted}
                            />
                        </Route>
                        {actor.roles.admin ?
                            <Route
                                path={`${PAGE_QUESTIONNAIRE}/:id`}
                                render={(props) =>
                                    <EditQuestionnairePage
                                        {...props}
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        questionnaires={questionnaires.entities}
                                        questions={questions.entities}
                                        filters={questionnaireFilters}
                                        onSaveQuestionnaire={questionnaires.onEntitySaved}
                                        onSaveQuestion={questions.onEntitySaved}
                                        onDeleteQuestion={questions.onEntityDeleted}
                                        onPrev={(questionnaireId) => router.redirectTo(history, PAGE_QUESTIONNAIRE + "/" + questionnaireId)}
                                        onNext={(questionnaireId) => router.redirectTo(history, PAGE_QUESTIONNAIRE + "/" + questionnaireId)}
                                    />}
                            /> : null}
                        {actor.roles.admin ?
                            <Route path={PAGE_TEMPLATES}>
                                <TemplatesPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    templates={templates.entities}
                                    filters={templateFilters}
                                    onDelete={templates.onEntityDeleted}
                                    onCopy={templates.onEntitySaved}
                                    onGotoAdd={() => router.redirectTo(history, PAGE_TEMPLATE)}
                                    onGotoEdit={(templateId) => router.redirectTo(history, `${PAGE_TEMPLATE}/${templateId}`)}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route exact path={PAGE_TEMPLATE}>
                                <AddTemplatePage
                                    snackbar={snackbar}
                                    submissionMergeFields={submissionMergeFields}
                                    onSave={(template) => {
                                        templates.onEntitySaved(template);
                                        router.redirectTo(history, PAGE_TEMPLATE + "/" + template.id);
                                    }}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route
                                path={`${PAGE_TEMPLATE}/:id`}
                                render={(props) =>
                                    <EditTemplatePage
                                        {...props}
                                        snackbar={snackbar}
                                        templates={templates.entities}
                                        filters={templateFilters}
                                        submissionMergeFields={submissionMergeFields}
                                        onSave={templates.onEntitySaved}
                                        onPrev={(templateId) => router.redirectTo(history, PAGE_TEMPLATE + "/" + templateId)}
                                        onNext={(templateId) => router.redirectTo(history, PAGE_TEMPLATE + "/" + templateId)}
                                    />}
                            /> : null}
                        {actor.roles.inventor ?
                            <Route path={PAGE_FILES}>
                                <FilesPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={actor}
                                    users={inventors}
                                    documents={documents.entities}
                                    files={files.entities}
                                    progress={chronicle.entity}
                                    filters={fileFilters}
                                    onSave={(saved) => {
                                        if (actor.roles.admin) {
                                            documents.onEntitySaved(saved);

                                        } else {
                                            files.onEntitySaved(saved);
                                        }
                                    }}
                                    onDelete={(deletedId) => {
                                        if (actor.roles.admin) {
                                            documents.onEntityDeleted(deletedId);

                                        } else {
                                            files.onEntityDeleted(deletedId);
                                        }
                                    }}
                                />
                            </Route> : null}
                        {actor.roles.admin || actor.roles.investor ?
                            <Route exact path={PAGE_USERS}>
                                <UsersPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={actor}
                                    users={decoratedUsers}
                                    filters={userFilters}
                                    documents={documents.entities}
                                    questionnaires={filteredQuestionnaires}
                                    questions={questions.entities}
                                    submissions={submissions.entities}
                                    progresses={progresses.entities}
                                    questionnaireRenderer={questionnaireRenderer}
                                    onDelete={users.onEntityDeleted}
                                    onGotoAdd={() => router.redirectTo(history, PAGE_USER)}
                                    onGotoProfile={() => router.redirectTo(history, PAGE_SETTINGS)}
                                    onGotoUser={(userId) => router.redirectTo(history, `${PAGE_USER}/${userId}`)}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route exact path={PAGE_USER}>
                                <AddUserPage
                                    snackbar={snackbar}
                                    onSave={(user) => {
                                        users.onEntitySaved(user);
                                        router.redirectTo(history, PAGE_USER + "/" + user.id);
                                    }}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route
                                path={`${PAGE_USER}/:id`}
                                render={(props) =>
                                    <EditUserPage
                                        {...props}
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        user={actor}
                                        users={decoratedUsers}
                                        documents={documents.entities}
                                        filters={userFilters}
                                        onSaveUser={users.onEntitySaved}
                                        onSaveDocument={documents.onEntitySaved}
                                        onDeleteDocument={documents.onEntityDeleted}
                                        onPrev={(userId) => router.redirectTo(history, PAGE_USER + "/" + userId)}
                                        onNext={(userId) => router.redirectTo(history, PAGE_USER + "/" + userId)}
                                    />}
                            /> : null}
                        {actor.roles.admin ?
                            <Route exact path={PAGE_CONTACTS}>
                                <ContactsPage
                                    snackbar={snackbar}
                                    dimensions={dimensions}
                                    user={actor}
                                    contacts={nodes.entities}
                                    filters={contactFilters}
                                    onDelete={nodes.onEntityDeleted}
                                    onGotoAdd={() => router.redirectTo(history, PAGE_CONTACT)}
                                    onGotoEdit={(contactId) => router.redirectTo(history, `${PAGE_CONTACT}/${contactId}`)}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route exact path={PAGE_CONTACT}>
                                <AddContactPage
                                    snackbar={snackbar}
                                    onSave={(node) => {
                                        nodes.onEntitySaved(node);
                                        router.redirectTo(history, PAGE_CONTACT + "/" + node.id);
                                    }}
                                />
                            </Route> : null}
                        {actor.roles.admin ?
                            <Route
                                path={`${PAGE_CONTACT}/:id`}
                                render={(props) =>
                                    <EditContactPage
                                        {...props}
                                        snackbar={snackbar}
                                        dimensions={dimensions}
                                        user={actor}
                                        contacts={nodes.entities}
                                        filters={userFilters}
                                        onSave={nodes.onEntitySaved}
                                        onPrev={(contactId) => router.redirectTo(history, PAGE_CONTACT + "/" + contactId)}
                                        onNext={(contactId) => router.redirectTo(history, PAGE_CONTACT + "/" + contactId)}
                                    />}
                            /> : null}
                        <Route path={PAGE_SETTINGS}>
                            <ProfilePage
                                snackbar={snackbar}
                                user={actor}
                                onSaveProfile={bootstrap.syncProfile}
                                onSaveNotifications={bootstrap.syncNotifications}
                                profileService={profileService}
                                notificationService={notificationService}
                                settingsForm={UserSettingsForm}
                                settingsProps={{
                                    admin: bootstrap.user.roles.admin,  // IMPORTANT: not actor, admins might have chosen another mode
                                }}
                                showImage={true}
                                showBirthday={true}
                                requirePhone={true}
                                allowApp={true}
                                allowEmail={true}
                                onDeleteProfile={onLogout}
                            />
                        </Route>
                        <Route path={PAGE_PASSWORD}>
                            <ChangePasswordPage
                                snackbar={snackbar}
                                user={bootstrap.user}
                                authService={authService}
                                onSaveProfile={bootstrap.syncProfile}
                                onToHome={() => router.redirectTo(history, home)}
                            />
                        </Route>
                        <Route path={PAGE_ABOUT}>
                            <AboutPage
                                appService={appService}
                                user={actor}
                                custom={<Logo/>}
                            />
                        </Route>
                        <Route path={PAGE_LOGIN}>
                            <Redirect to={home}/>
                        </Route>
                        <Route>
                            <NotFoundPage/>
                        </Route>
                    </Switch>}
                </div>
                {hasNoRoles ? null :
                    <Footer
                        dimensions={dimensions}
                        user={actor}
                        onLogout={onLogout}
                        onHelp={() => setShowHelp(true)}
                        numNotifications={notifications.entities.filter(notificationUtils.isNewNotification).length}
                    />}
                {showHelp && !hasNoRoles ?
                    <HelpDialog
                        snackbar={snackbar}
                        onCancel={() => setShowHelp(false)}
                    /> : null}
            </> :
            <>
                <PublicHeader
                    dimensions={dimensions}
                    custom={<Logo/>}
                />
                <div className="public-content">
                    <Switch>
                        <Route path={PAGE_LOGIN}>
                            <LoginPage
                                snackbar={snackbar}
                                authService={authService}
                                onForgotPassword={() => router.redirectTo(history, PAGE_RECOVERY)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={PAGE_RECOVERY}>
                            <ForgotPasswordPage
                                snackbar={snackbar}
                                authService={authService}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                                appName={app.name}
                            />
                        </Route>
                        <Route path={PAGE_THANK_YOU}>
                            <ThankYouPage
                                snackbar={snackbar}
                                onBackToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                            />
                        </Route>
                        <Route path={`${PATH_DEEP_LINK}${PAGE_WELCOME}`}>
                            <WelcomePage
                                snackbar={snackbar}
                                authService={authService}
                                onToLogin={() => router.redirectTo(history, PAGE_LOGIN)}
                                onLogin={onLogin}
                            />
                        </Route>
                    </Switch>
                </div>
            </>}
        </div>
}

export default withRouter(App);
